/**
 * Gesprochene Abbiegehinweise.
 *
 * Über die Stimme, die im Gerät ohnehin steckt — kein Dienst, keine Datei, kein
 * Netz. Das ist zugleich die einzige Art, die mit Abschnitt 5 des Leitfadens
 * vereinbar ist: Ein Hinweis, der gesprochen werden soll, verrät sonst jemandem
 * draußen, wo der Fahrer gerade abbiegt.
 *
 * Die Ansage ist eine Zugabe, keine Voraussetzung: Alles steht zugleich auf dem
 * Bildschirm, und ohne Ton bleibt die App vollständig bedienbar (Abschnitt 2).
 * Sie ist deshalb voreingestellt **aus** und wird vom Fahrer eingeschaltet.
 *
 * Eine Eigenheit von iOS bestimmt den Zuschnitt: Die erste Äußerung muss
 * unmittelbar aus einem Fingertipp hervorgehen, sonst bleibt die Stimme für den
 * Rest der Sitzung stumm. Deshalb `weckeStimme` — beim Einschalten und beim
 * Starten der Navigation aufgerufen, beides Fingertipps.
 */
const SPEICHER = 'konvoi.ansage';

export class Ansage {
  #an: boolean;
  #geweckt = false;

  constructor() {
    this.#an = leseEinstellung();
  }

  /** Kann dieses Gerät überhaupt sprechen? */
  get verfügbar(): boolean {
    return typeof window !== 'undefined' && 'speechSynthesis' in window;
  }

  get an(): boolean {
    return this.#an && this.verfügbar;
  }

  setAn(an: boolean): void {
    this.#an = an;
    try {
      window.localStorage.setItem(SPEICHER, an ? 'an' : 'aus');
    } catch {
      // Ohne Speicher bleibt die Einstellung für diese Fahrt bestehen. Das ist
      // ärgerlich, aber kein Grund, die Ansage abzulehnen.
    }
    if (!an) {
      this.schweig();
    }
  }

  /**
   * Bringt die Stimme in Gang, solange der Fingertipp noch nachwirkt.
   *
   * Ohne diesen Anstoß aus einer Bedienhandlung heraus spricht iOS später
   * nichts — und zwar wortlos, ohne Fehler.
   */
  weckeStimme(): void {
    if (!this.an || this.#geweckt) {
      return;
    }
    this.#geweckt = true;
    this.sprich('Navigation beginnt.');
  }

  sprich(text: string): void {
    if (!this.an) {
      return;
    }

    const äußerung = new SpeechSynthesisUtterance(text);
    äußerung.lang = 'de-DE';
    // Etwas langsamer als die Vorgabe: Im fahrenden Auto mit offenem Fenster
    // geht ein hastig gesprochener Straßenname unter.
    äußerung.rate = 0.95;
    window.speechSynthesis.speak(äußerung);
  }

  /** Bricht ab, was gerade gesprochen wird. */
  schweig(): void {
    if (this.verfügbar) {
      window.speechSynthesis.cancel();
    }
  }
}

function leseEinstellung(): boolean {
  try {
    return window.localStorage.getItem(SPEICHER) === 'an';
  } catch {
    return false;
  }
}
